import type { RouteRecordRaw } from 'vue-router'
import { createRouter, createWebHistory } from 'vue-router'
import { beforeEach } from './guards'

/* ── Public default-layout ───────────────────────────────────────────────── */
const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('~/pages/Home.vue'),
    meta: {
      layout: 'default',
      requiresAuth: false,
      requiresOrganization: false,
      title: 'docQ — Document Q&A you can build on',
    },
  },
  {
    path: '/contact',
    component: () => import('~/pages/Contact.vue'),
    meta: {
      layout: 'default',
      requiresAuth: false,
      requiresOrganization: false,
      title: 'Contact — docQ',
    },
  },
  {
    path: '/privacy',
    component: () => import('~/pages/Privacy.vue'),
    meta: {
      layout: 'default',
      requiresAuth: false,
      requiresOrganization: false,
      title: 'Privacy — docQ',
    },
  },
  {
    path: '/refunds',
    component: () => import('~/pages/Refunds.vue'),
    meta: {
      layout: 'default',
      requiresAuth: false,
      requiresOrganization: false,
      title: 'Refund Policy — docQ',
    },
  },
  {
    path: '/terms',
    component: () => import('~/pages/Terms.vue'),
    meta: {
      layout: 'default',
      requiresAuth: false,
      requiresOrganization: false,
      title: 'Terms of Service — docQ',
    },
  },

  /* ── Docs layout ─────────────────────────────────────────────────────────── */
  {
    path: '/developers',
    component: () => import('~/pages/developers/index.vue'),
    meta: {
      layout: 'docs',
      requiresAuth: false,
      requiresOrganization: false,
      title: 'Developer Portal — docQ',
    },
  },
  {
    path: '/developers/getting-started',
    component: () => import('~/pages/developers/getting-started.vue'),
    meta: {
      layout: 'docs',
      requiresAuth: false,
      requiresOrganization: false,
      title: 'Getting Started — docQ Developers',
    },
  },
  {
    path: '/developers/authentication',
    component: () => import('~/pages/developers/authentication.vue'),
    meta: {
      layout: 'docs',
      requiresAuth: false,
      requiresOrganization: false,
      title: 'Authentication — docQ Developers',
    },
  },
  {
    path: '/developers/api-reference',
    component: () => import('~/pages/developers/api-reference.vue'),
    meta: {
      layout: 'docs',
      requiresAuth: false,
      requiresOrganization: false,
      title: 'API Reference — docQ Developers',
    },
  },
  {
    path: '/developers/faq',
    component: () => import('~/pages/developers/faq.vue'),
    meta: {
      layout: 'docs',
      requiresAuth: false,
      requiresOrganization: false,
      title: 'FAQ — docQ Developers',
    },
  },
  {
    path: '/developers/playground',
    component: () => import('~/pages/developers/playground.vue'),
    meta: {
      layout: 'docs',
      requiresAuth: false,
      requiresOrganization: false,
      title: 'API Playground — docQ Developers',
    },
  },

  /* ── No-layout auth ──────────────────────────────────────────────────────── */
  {
    path: '/auth',
    component: () => import('~/pages/Auth.vue'),
    meta: {
      layout: undefined,
      requiresAuth: false,
      requiresOrganization: false,
      title: 'Authentication — docQ',
    },
  },

  /* ── No-layout auth + org ────────────────────────────────────────────────── */
  {
    path: '/app',
    component: () => import('~/pages/AppChat.vue'),
    meta: {
      layout: undefined,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Chat — docQ',
    },
  },
  {
    path: '/app/settings',
    component: () => import('~/pages/AppSettings.vue'),
    meta: {
      layout: undefined,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Settings — docQ',
    },
  },
  {
    path: '/org',
    component: () => import('~/pages/Org.vue'),
    meta: {
      layout: undefined,
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Organization — docQ',
    },
  },

  /* ── No-layout auth-only ─────────────────────────────────────────────────── */
  {
    path: '/onboarding',
    component: () => import('~/pages/Onboarding.vue'),
    meta: {
      layout: undefined,
      requiresAuth: true,
      requiresOrganization: false,
      title: 'Welcome — docQ',
    },
  },
  {
    path: '/join/:code',
    component: () => import('~/pages/JoinCode.vue'),
    meta: {
      layout: undefined,
      requiresAuth: true,
      requiresOrganization: false,
      title: 'Join Organization — docQ',
    },
  },
  {
    path: '/infographic',
    component: () => import('~/pages/Infographic.vue'),
    meta: {
      layout: undefined,
      requiresAuth: true,
      requiresOrganization: false,
      title: 'Visual Summary — docQ',
    },
  },
  {
    path: '/deck',
    component: () => import('~/pages/Deck.vue'),
    meta: {
      layout: undefined,
      requiresAuth: true,
      requiresOrganization: false,
      title: 'Deck — docQ',
    },
  },
  {
    path: '/app/infographic-templates',
    component: () => import('~/pages/AppInfographicTemplates.vue'),
    meta: {
      layout: undefined,
      requiresAuth: true,
      requiresOrganization: false,
      title: 'Infographic Templates — docQ',
    },
  },

  /* ── Redirect ────────────────────────────────────────────────────────────── */
  {
    path: '/workspace',
    redirect: '/org',
  },

  /* ── Catch-all 404 ───────────────────────────────────────────────────────── */
  {
    path: '/:pathMatch(.*)*',
    component: () => import('~/pages/NotFound.vue'),
    meta: {
      layout: 'default',
      requiresAuth: false,
      requiresOrganization: false,
      title: 'Not Found — docQ',
    },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to) {
    if (to.hash) return { el: to.hash, top: 52, behavior: 'smooth' }
    return false
  },
})

router.beforeEach(beforeEach)

export default router
